import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import { FiToggleRight, FiToggleLeft } from 'react-icons/fi';
import '../../../css/pages.css';

// Motion component aliases
const MotionDiv = motion.div;
const MotionH2 = motion.h2;
const MotionP = motion.p;

const PricingCard = ({ 
  title, 
  price, 
  yearlyPrice = null, 
  features, 
  isEnterprise = false,
  isFeatured = false,
  isYearly = false,
  cardVariants
}) => {  
  const savings = yearlyPrice ? ((price * 12) - (yearlyPrice * 12)).toFixed(2) : 0;
  const displayPrice = isYearly && yearlyPrice ? yearlyPrice : price;
  
  return (
    <MotionDiv 
      className={`pricing-card ${isFeatured ? 'featured' : ''} ${isEnterprise ? 'enterprise' : ''}`}
      variants={cardVariants}
      whileHover={{ 
        y: -15, 
        scale: isFeatured ? 1.08 : 1.05,
        transition: { duration: 0.3 }
      }}
    >
      <h3 className="card-title">{title}</h3>
      <div className="price-section">
        <div className="price">
          {isEnterprise ? 'Custom' : (
            <>
              ${displayPrice}
              <span className="price-period">/month</span>
            </>
          )}
        </div>
        {!isEnterprise && isYearly && yearlyPrice && savings > 0 && (
          <div className="savings-text">
            Save ${savings} per year
          </div>
        )}
      </div>
      <ul className="features-list">
        {features.map((feature, index) => (
          <li key={index} className="feature-item">
            <FaCheck className="check-icon" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <Link 
        to={isEnterprise ? '/request-demo' : '/getstarted'} 
        className="action-button"
      >
        {isEnterprise ? 'Contact Sales' : 'Get Started'}
      </Link>
    </MotionDiv>
  );
};

const Pricing = () => {
  const [isYearly, setIsYearly] = useState(false);

  const plans = [
    {
      title: 'Starter',
      price: 0,
      yearlyPrice: '0',
      features: [
        'Essential POS features',
        'Basic transaction tracking',
        'Limited inventory management',
        'Basic sales insights',
        'Up to 30 products',
        '5GB Storage',
        'Community support'
      ]
    },
    {
      title: 'Professional',
      price: 8.99,
      yearlyPrice: 8.09,
      isFeatured: true,
      features: [
        'Advanced sales analytics',
        'Real-time monitoring',
        'Enhanced inventory management',
        'Up to 100 products',
        'Custom receipt templates',
        '20GB Storage',
        'Email support (24h response)'
      ]
    },
    {
      title: 'Business',
      price: 14.99,
      yearlyPrice: 13.49,
      features: [
        'AI-powered recommendations',
        'Comprehensive reporting',
        'Advanced customer management',
        'Up to 500 products',
        'Automated tax calculations',
        '50GB Storage',
        'Priority support'
      ]
    },
    {
      title: 'Enterprise',
      price: 0,
      yearlyPrice: 0,
      isEnterprise: true,
      features: [
        'Full platform customization',
        'Custom AI integration',
        'Unlimited products',
        'Advanced security controls',
        'Multi-location management',
        'Unlimited Storage',
        '24/7 dedicated support'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { y: 80, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="pricing">
      <MotionDiv 
        className="pricing-container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="section-title">
          <MotionH2 
            className="pricing-title"
            variants={itemVariants}
          >
            Choose Your Plan
          </MotionH2>

          <div className="pricing-header">
            <MotionDiv 
              className="main-title"
              variants={itemVariants}
            >
              What if every business could have a personal AI assistant, available 24/7?
            </MotionDiv>
            <MotionP 
              className="subtitle"
              variants={itemVariants}
            >
              POSlah doesn't just process transactions – it understands your needs. 
              It spots inefficiencies before they become problems. It frees up your 
              time to focus on growth. And it turns data into business success.
            </MotionP>
            
            <MotionDiv 
              className="toggle-container"
              variants={itemVariants}
            >
              <span className={!isYearly ? 'active' : ''}>Monthly</span>
              <motion.button 
                className="toggle-button"
                onClick={() => setIsYearly(!isYearly)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {isYearly ? <FiToggleRight size={24} /> : <FiToggleLeft size={24} />}
              </motion.button>
              <span className={isYearly ? 'active' : ''}>Yearly (Save 10%)</span>
            </MotionDiv>
          </div>
        </div>

        <div className="pricing-grid">
          {plans.map((plan, index) => (
            <PricingCard
              key={index}
              {...plan}
              isYearly={isYearly}
              cardVariants={cardVariants}
            />
          ))}
        </div>
      </MotionDiv>
    </div>
  );
};

export default Pricing;